export default class HeroAnimation {
  constructor(selector = '.hero-animation') {
    this.container = document.querySelector(selector)
    if (!this.container) {
      return
    }
    this.getRandom = this.seededRandom(window.location.pathname)
    this.hasStarted = false
    // Check if we're on homepage (inside .homepage-container)
    this.isHomepage = !!this.container.closest('.homepage-container')
  }

  seededRandom(str) {
    let seed = 0
    for (let i = 0; i < str.length; i++) {
      seed = (seed * 31 + str.charCodeAt(i)) >>> 0
    }

    // Park-Miller requires a seed in the range 1..2147483646.
    seed = (seed % 2147483646) + 1

    return () => {
      // The two numbers are part of the seeding algorythm
      seed = (seed * 16807) % 2147483647
      return (seed - 1) / 2147483646
    }
  }

  generatePositions() {
    // Square size: 150x150
    const squareHalfWidth = 75

    // Use different viewBox dimensions based on context
    // Homepage: -300 -150 600 300 (2:1 ratio)
    // Other pages: -300 -225 600 450 (4:3 ratio - 50% taller)
    const viewBoxHeight = this.isHomepage ? 150 : 225

    // Define corner positions with offset from edges
    const edgeOffset = 40
    const corners = {
      topRight: { x: 300 - squareHalfWidth - edgeOffset, y: -viewBoxHeight + squareHalfWidth + edgeOffset },
      topLeft: { x: -300 + squareHalfWidth + edgeOffset, y: -viewBoxHeight + squareHalfWidth + edgeOffset },
      bottomRight: { x: 300 - squareHalfWidth - edgeOffset, y: viewBoxHeight - squareHalfWidth - edgeOffset },
      bottomLeft: { x: -300 + squareHalfWidth + edgeOffset, y: viewBoxHeight - squareHalfWidth - edgeOffset }
    }

    // Starting position: all squares stacked in top-right corner with 10px offset per layer
    // ACTUAL colors: square-one (violet = darkest), square-two (violet-200 = lightest), square-three (violet-300 = middle)
    // Rendering order set in init(): square-three → square-two → square-one (so square-one on top)
    // Visual stack (front to back): darkest on top → middle → lightest at back
    // Offset: left and DOWN (positive Y) from top-right corner
    const stackOffset = 10
    const startingPositions = [
      // Position 0 for square-one (violet = DARKEST): NO offset (on top, front)
      { x: corners.topRight.x, y: corners.topRight.y },
      // Position 1 for square-two (violet-200 = LIGHTEST): most offset (back layer)
      { x: corners.topRight.x - stackOffset * 2, y: corners.topRight.y + stackOffset * 2 },
      // Position 2 for square-three (violet-300 = MIDDLE): medium offset (middle layer)
      { x: corners.topRight.x - stackOffset, y: corners.topRight.y + stackOffset }
    ]

    // Determine which square stays fixed and which corner it goes to
    // Use seeded random to pick fixed square (0, 1, or 2)
    const fixedSquareIndex = Math.floor(this.getRandom() * 3)

    // Pick a corner for the fixed square (use seeded random)
    const cornerNames = ['topLeft', 'bottomLeft', 'bottomRight']
    const fixedCornerName = cornerNames[Math.floor(this.getRandom() * cornerNames.length)]
    const fixedCorner = corners[fixedCornerName]

    // Determine diagonal opposite corner for overlapping squares
    const oppositeCorners = {
      topLeft: 'bottomRight',
      bottomLeft: 'topRight',
      bottomRight: 'topLeft',
      topRight: 'bottomLeft'
    }
    const overlapCornerName = oppositeCorners[fixedCornerName]
    const overlapCorner = corners[overlapCornerName]

    // Create end positions array
    const endPositions = []
    const overlapSquares = []

    // Define safe boundaries to keep squares fully within viewBox
    const safeZone = {
      minX: -300 + squareHalfWidth + 20,
      maxX: 300 - squareHalfWidth - 20,
      minY: -viewBoxHeight + squareHalfWidth + 20,
      maxY: viewBoxHeight - squareHalfWidth - 20
    }

    // Assign positions
    for (let i = 0; i < 3; i++) {
      if (i === fixedSquareIndex) {
        // This square goes alone to the fixed corner
        const variation = 10
        let x = fixedCorner.x + (this.getRandom() * variation - variation / 2)
        let y = fixedCorner.y + (this.getRandom() * variation - variation / 2)

        // Ensure within boundaries
        x = Math.max(safeZone.minX, Math.min(safeZone.maxX, x))
        y = Math.max(safeZone.minY, Math.min(safeZone.maxY, y))

        endPositions[i] = { x, y }
      } else {
        // These squares overlap in the opposite corner
        overlapSquares.push(i)
      }
    }

    // Position the two overlapping squares with significant overlap
    // Random z-order (which one is on top visually)
    const topSquareIndex = overlapSquares[Math.floor(this.getRandom() * 2)]

    overlapSquares.forEach(squareIdx => {
      const isOnTop = squareIdx === topSquareIndex
      // Random overlap amount between 30-70px for the top square
      const overlapOffset = isOnTop ? (30 + this.getRandom() * 40) : 0
      // Small variation around the base position
      const variation = 8

      let x = overlapCorner.x + overlapOffset + (this.getRandom() * variation - variation / 2)
      let y = overlapCorner.y + overlapOffset + (this.getRandom() * variation - variation / 2)

      // Ensure squares stay within safe boundaries
      x = Math.max(safeZone.minX, Math.min(safeZone.maxX, x))
      y = Math.max(safeZone.minY, Math.min(safeZone.maxY, y))

      endPositions[squareIdx] = { x, y }
    })

    // Build the final position data with staggered delays
    return startingPositions.map((start, i) => {
      const end = endPositions[i]

      return {
        startX: start.x,
        startY: start.y,
        startRotation: 0, // Start with no rotation
        startScale: 1,
        endX: end.x,
        endY: end.y,
        endRotation: 0, // No rotation at end
        endScale: 1,
        delay: i * 1 // Staggered delay: 0s, 1s, 2s (roughly 1/3 of 3s animation)
      }
    })
  }

  createSVG() {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    // Use different viewBox based on context
    // Homepage: 2:1 ratio (600x300), Other pages: 4:3 ratio (600x450)
    const viewBoxHeight = this.isHomepage ? 300 : 450
    const viewBoxY = this.isHomepage ? -150 : -225
    svg.setAttribute('viewBox', `-300 ${viewBoxY} 600 ${viewBoxHeight}`)
    svg.setAttribute('preserveAspectRatio', 'xMidYMid meet')
    svg.classList.add('hero-squares')
    return svg
  }

  createSquare(className, position) {
    const square = document.createElementNS('http://www.w3.org/2000/svg', 'rect')
    // Center the rectangles by offsetting them by half their width/height
    square.setAttribute('width', '150')
    square.setAttribute('height', '150')
    square.setAttribute('x', '-75')  // -width/2 to center it
    square.setAttribute('y', '-75')  // -height/2 to center it
    square.setAttribute('rx', '10')
    square.classList.add(className, 'animated-square')

    // Set all the CSS custom properties
    Object.entries(position).forEach(([key, value]) => {
      square.style.setProperty(
        `--${key}`,
        typeof value === 'number'
          ? (key.includes('Rotation')
              ? `${value}deg`
              : key.includes('Scale')
                ? value
                : key === 'delay'
                  ? `${value}s`
                  : `${value}px`)
          : value,
      )
    })

    return square
  }

  init() {
    if (!this.container) return

    const svg = this.createSVG()
    const positions = this.generatePositions()

    // Render lightest first, darkest last (so darkest is on top in SVG)
    // ACTUAL colors: square-one (darkest), square-two (lightest), square-three (middle)
    // Order: lightest (back) → middle → darkest (front/top)
    const renderOrder = [
      { className: 'square-two', positionIndex: 1 },   // lightest - rendered first (back)
      { className: 'square-three', positionIndex: 2 }, // middle - rendered second
      { className: 'square-one', positionIndex: 0 }    // darkest - rendered last (on top)
    ]

    renderOrder.forEach(({ className, positionIndex }) => {
      const square = this.createSquare(className, positions[positionIndex])
      svg.appendChild(square)
    })

    this.container.appendChild(svg)

    // Set up intersection observer to start animation when in viewport
    this.observeViewport()
  }

  observeViewport() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.hasStarted) {
          this.hasStarted = true
          // Add class to trigger animation
          this.container.classList.add('animate-in')
          // Disconnect observer after animation starts
          observer.disconnect()
        }
      })
    }, {
      threshold: 0.2 // Start when 20% of the element is visible
    })

    observer.observe(this.container)
  }
}
